<script setup lang="ts">
import {computed, onMounted, ref} from 'vue'
import {useDialogs, useLocalization} from '@hantera/portal-app'
import {PhoneInput, TextInput, useClose} from '@hantera/design-system'
import PasswordDisplayModal from './PasswordDisplayModal.vue'
import {IamApiError} from '@/backend/iam.types';

const props = defineProps<{
  onCreated?: (principal: any) => void
  onCancel?: () => void
  iamService: any
}>()

const t = useLocalization('iam.principals')
const dialogs = useDialogs()
const close = useClose()

const step = ref(1)
const isCreating = ref(false)
const errorMessage = ref('')
const availableRoles = ref<any[]>([])
const isLoadingRoles = ref(false)
const nameElem = ref()

const form = ref({
  name: '',
  email: '',
  phone: '',
  roles: [] as string[],
})

const passwordDelivery = ref<'email' | 'display' | null>(null)

const isStep1Valid = computed(() => {
  if (!form.value.name.trim()) return false
  if (!form.value.email.trim()) return false

  // Validate email format
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailPattern.test(form.value.email)) return false

  return true
})

const isStep3Valid = computed(() => {
  return passwordDelivery.value !== null
})

// Computed properties for role selection
const selectedRoles = computed(() => {
  return form.value.roles
    .map((key) => availableRoles.value.find((r) => r.key === key))
    .filter((r) => r !== undefined)
})

const unselectedRoles = computed(() => {
  return availableRoles.value.filter((r) => !form.value.roles.includes(r.key))
})

function addRole(roleKey: string) {
  if (!form.value.roles.includes(roleKey)) {
    form.value.roles.push(roleKey)
  }
}

function removeRole(roleKey: string) {
  const index = form.value.roles.indexOf(roleKey)
  if (index > -1) {
    form.value.roles.splice(index, 1)
  }
}

onMounted(async () => {
  await loadRoles()
  nameElem.value?.focus()
})

async function loadRoles() {
  isLoadingRoles.value = true
  try {
    const response = await props.iamService.getRoles({ limit: 100 })
    availableRoles.value = response.roles || []
  } catch (error) {
    console.error('Failed to load roles:', error)
    errorMessage.value = 'Failed to load roles'
    availableRoles.value = []
  } finally {
    isLoadingRoles.value = false
  }
}

async function create() {
  if (!isStep3Valid.value) return

  errorMessage.value = ''
  isCreating.value = true

  try {
    // Create principal with roles
    const principal = await props.iamService.createPrincipal({
      name: form.value.name.trim(),
      email: form.value.email.trim(),
      phone: form.value.phone.trim() || undefined,
      roles: form.value.roles,
    })

    // Reset password based on delivery choice
    const sendEmail = passwordDelivery.value === 'email'
    const passwordResponse = await props.iamService.resetPassword(principal.id, sendEmail)

    if (passwordDelivery.value === 'display' && passwordResponse.temporaryPassword) {
      // Show password display modal
      dialogs.openModal(
        t('create.passwordDisplay.title'),
        PasswordDisplayModal,
        false, // not closable via X
        {
          password: passwordResponse.temporaryPassword,
          userName: principal.name,
          userEmail: principal.email,
          onClose: () => {
            props.onCreated?.(principal)
          },
        }
      )
    } else {
      // Email delivery - just notify success
      props.onCreated?.(principal)
    }
    close()
  } catch (error: any) {

    // Backend returns: { errors: [{ code, message, details }] }
    if (error instanceof IamApiError) {
      const errorCode = error.code
      const apiMessage = error.message

      // Try to get localized message for this error code
      const localizedKey = `create.errors.${errorCode}`
      const localizedMsg = t(localizedKey)

      // If localization exists, use it; otherwise use API message
      if (localizedMsg !== localizedKey) {
        errorMessage.value = localizedMsg
      } else {
        errorMessage.value = apiMessage ?? errorCode
      }
    } else {
      errorMessage.value = t('create.errors.UNKNOWN')
    }
    console.error('Failed to create principal:', error)
    isCreating.value = false
  }
}

function cancel() {
  props.onCancel?.()
}

function nextStep() {
  if (step.value === 1 && isStep1Valid.value) {
    step.value = 2
  } else if (step.value === 2) {
    step.value = 3
  }
}

function previousStep() {
  if (step.value > 1) {
    step.value--
  }
}
</script>

<template>
  <div class="create-principal p2">
    <!-- Step indicator -->
    <div class="step-progress">
      <div class="step-item" :class="{ active: step === 1, completed: step > 1 }">
        <span class="step-number">1</span>
        <span class="step-label">{{ t('create.steps.basic') }}</span>
      </div>
      <div class="step-item" :class="{ active: step === 2, completed: step > 2 }">
        <span class="step-number">2</span>
        <span class="step-label">{{ t('create.steps.roles') }}</span>
      </div>
      <div class="step-item" :class="{ active: step === 3 }">
        <span class="step-number">3</span>
        <span class="step-label">{{ t('create.steps.password') }}</span>
      </div>
    </div>

    <!-- Step 1: Basic info -->
    <div v-if="step === 1" class="wizard-step">
      <div class="form-field">
        <label>{{ t('profile.name') }} *</label>
        <TextInput
          v-model="form.name"
          :placeholder="t('create.form.namePlaceholder')"
          ref="nameElem"
        />
      </div>

      <div class="form-field">
        <label>{{ t('profile.email') }} *</label>
        <TextInput
          v-model="form.email"
          type="email"
          :placeholder="t('create.form.emailPlaceholder')"
        />
      </div>

      <div class="form-field">
        <label>{{ t('profile.phone') }}</label>
        <PhoneInput
          v-model="form.phone"
          :placeholder="t('create.form.phonePlaceholder')"
        />
      </div>
    </div>

    <!-- Step 2: Roles -->
    <div v-else-if="step === 2" class="wizard-step step-roles">
      <div v-if="isLoadingRoles" class="loading-state">
        {{ t('status.loading') }}...
      </div>

      <div v-else-if="availableRoles.length === 0" class="empty-state">
        {{ t('create.noRolesAvailable') }}
      </div>

      <div v-else class="roles-selection">
        <!-- Selected roles section -->
        <div class="roles-section">
          <div class="section-header">
            <h4>{{ t('create.selectedRoles') }}</h4>
            <span class="count-badge">{{ selectedRoles.length }}</span>
          </div>

          <div v-if="selectedRoles.length === 0" class="empty-hint">
            {{ t('create.noRolesSelected') }}
          </div>

          <div v-else class="roles-list">
            <div v-for="role in selectedRoles" :key="role!.key" class="role-card box">
              <div class="role-info">
                <div class="role-name">{{ role!.key }}</div>
                <div v-if="role!.description" class="role-description">{{ role!.description }}</div>
              </div>
              <button @click="removeRole(role!.key)" class="btn-danger btn-sm">
                {{ t('roles.remove') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Available roles section -->
        <div v-if="unselectedRoles.length > 0" class="roles-section">
          <div class="section-header">
            <h4>{{ t('roles.availableRoles') }}</h4>
          </div>

          <div class="roles-list">
            <div v-for="role in unselectedRoles" :key="role.key" class="role-card box">
              <div class="role-info">
                <div class="role-name">{{ role.key }}</div>
                <div v-if="role.description" class="role-description">{{ role.description }}</div>
              </div>
              <button @click="addRole(role.key)" class="btn-secondary btn-sm">
                {{ t('roles.add') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 3: Password delivery -->
    <div v-else-if="step === 3" class="wizard-step">
      <p class="help-text">{{ t('create.passwordHelp') }}</p>

      <label class="radio-option">
        <input type="radio" value="email" v-model="passwordDelivery" />
        <div class="option-content">
          <strong>{{ t('create.delivery.emailTitle') }}</strong>
          <p>{{ t('create.delivery.emailDesc', { email: form.email }) }}</p>
          <span class="badge badge-success">{{ t('create.delivery.recommended') }}</span>
        </div>
      </label>

      <label class="radio-option">
        <input type="radio" value="display" v-model="passwordDelivery" />
        <div class="option-content">
          <strong>{{ t('create.delivery.displayTitle') }}</strong>
          <p>{{ t('create.delivery.displayDesc') }}</p>
        </div>
      </label>
    </div>

    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <!-- Navigation buttons -->
    <div class="dialog-actions">
      <button
        v-if="step > 1"
        @click="previousStep"
        class="btn-secondary"
        :disabled="isCreating"
      >
        {{ t('actions.back') }}
      </button>

      <button v-if="step < 3" @click="nextStep" class="btn-primary" :disabled="step === 1 && !isStep1Valid">
        {{ t('actions.next') }}
      </button>

      <button
        v-if="step === 3"
        @click="create"
        class="btn-primary"
        :disabled="!isStep3Valid || isCreating"
      >
        {{ isCreating ? t('actions.creating') : t('actions.createUser') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.create-principal {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 500px;
}

.step-progress {
  display: flex;
  justify-content: space-between;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--color-border);
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  color: var(--color-text-tertiary);
}

.step-item.active {
  color: var(--color-primary);
}

.step-item.completed {
  color: var(--color-success);
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid currentColor;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  background: var(--color-surface);
}

.step-item.active .step-number {
  background: var(--color-primary);
  color: white;
  border-color: var(--color-primary);
}

.step-item.completed .step-number {
  background: var(--color-success);
  color: white;
  border-color: var(--color-success);
}

.step-label {
  font-size: 0.8125rem;
  text-align: center;
}

.wizard-step {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 300px;
}

.help-text {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin: 0 0 0.5rem 0;
}

.loading-state,
.empty-state {
  padding: 2rem;
  text-align: center;
  color: var(--color-text-secondary);
}

.step-roles {
  min-height: 400px;
}

.roles-selection {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.roles-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.section-header h4 {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.375rem;
  border-radius: 10px;
  background: var(--color-primary);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
}

.empty-hint {
  padding: 1rem;
  text-align: center;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  font-style: italic;
}

.roles-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 250px;
  overflow-y: auto;
}

.role-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  gap: 1rem;
}

.role-info {
  flex: 1;
  min-width: 0;
}

.role-name {
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--color-text-primary);
  margin-bottom: 0.25rem;
}

.role-description {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
  white-space: nowrap;
}

.radio-option {
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.radio-option:hover {
  background: var(--color-hover);
  border-color: var(--color-primary);
}

.radio-option input[type='radio'] {
  margin-top: 2px;
  flex-shrink: 0;
}

.option-content {
  flex: 1;
}

.option-content strong {
  display: block;
  margin-bottom: 0.5rem;
}

.option-content p {
  margin: 0 0 0.5rem 0;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
}

.badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.badge-success {
  background: var(--color-success-bg);
  color: var(--color-success);
}

.error-message {
  color: var(--color-error);
  background: var(--color-error-bg);
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-border);
}
</style>
